// Define the interface for the StepIndicator component's props
interface StepIndicatorProps {
  steps: string[]; // An array of step labels
  currentStep: number; // The current active step (1-based index)
}

// Functional component for rendering a step progress indicator
export function StepIndicator({ steps, currentStep }: StepIndicatorProps) {
  return (
    <div className="py-8">
      {/* Container for centering the step indicator */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <nav aria-label="Progress">
          {/* Ordered list of steps */}
          <ol className="flex items-center justify-center space-x-8 md:space-x-12">
            {steps.map((step, index) => (
              <li key={step} className="relative md:flex-1">
                {/* Render a connecting line for steps after the first */}
                {index > 0 && (
                  <div className="hidden md:block absolute top-1/2 left-0 -translate-y-1/2 w-full">
                    <div
                      className={`h-1 rounded-full ${
                        index < currentStep
                          ? 'bg-primary-main' // Line is active (completed step)
                          : 'bg-background-light' // Line is inactive (future step)
                      }`}
                    />
                  </div>
                )}

                {/* Step Circle and Label */}
                <div className="flex flex-col items-center group">
                  <span
                    className={`w-10 h-10 flex items-center justify-center rounded-full font-semibold shadow-md ${
                      index + 1 === currentStep
                        ? 'bg-primary-main text-white border-2 border-primary-dark' // Active step
                        : index + 1 < currentStep
                        ? 'bg-accent-success text-white border-2 border-accent-success-dark' // Completed step
                        : 'bg-background-light text-text-secondary border border-gray-300' // Future step
                    }`}
                  >
                    {/* Display step number */}
                    {index + 1}
                  </span>
                  <span
                    className={`mt-2 text-sm font-medium ${
                      index + 1 <= currentStep
                        ? 'text-primary-dark' // Label for active or completed steps
                        : 'text-text-secondary' // Label for future steps
                    }`}
                  >
                    {step} {/* Step label */}
                  </span>
                </div>
              </li>
            ))}
          </ol>
        </nav>
      </div>
    </div>
  );
}
